import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Calculator, Plus, Save, Send, Trash2 } from 'lucide-react'
import {
  Alert, Card, Field, Input, Loading, Select, Textarea, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, ConversionNote, MaterialPicker, NotPermitted, queueAttachments,
  type PendingAttachment,
} from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { fetchLines, useMaterials, useSites, useTerms, useVendors } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import {
  can, gstTreatmentFor, iso, needsConversion, round2, round3, splitGst, stockToPurchase, purchaseToStock,
  stockRateToPurchaseRate,
} from '@/lib/domain'
import { amountInWords, qty, rupees } from '@/lib/format'
import { useCompanySettings } from '@/lib/settings'
import { enqueue, reviseDocument } from '@/lib/sync'
import type { Material, Site, Vendor } from '@/lib/types'

interface OpenPrLine {
  pr_line_id: string
  pr_id: string
  site_id: string
  sl_no: number
  material_id: string
  item_code: string
  item_name: string
  hsn_code: string | null
  stock_uom: string
  purchase_uom: string
  conversion_factor: number
  gst_rate: number
  quantity_open: number
  estimated_rate: number
  specifications: string | null
}

interface PoLineDraft {
  key: string
  pr_line_id: string | null
  material_id: string | null
  /** Quantity in the STOCK uom — what the store-man will count on arrival. */
  quantity_stock: string
  /** Quantity in the PURCHASE uom — what is printed on the PO and the challan. */
  quantity_purchase: string
  unit_rate: string
  gst_rate_percent: string
  specifications: string
  /** Where the suggested rate came from, shown under the rate box. */
  rate_hint?: string | null
}

/**
 * The buyer's standing explanation of why this vendor was chosen.
 *
 * INTERNAL RECORD ONLY. It is never printed on the purchase order — the PDF's
 * input type has no field for it, and generate-po-pdf refuses to build a
 * document if the text ever reaches its payload. Printing it would hand the
 * vendor the company's negotiating position.
 *
 * It is a starting point, not a form of words to be left alone: the buyer is
 * expected to edit it when the story on a particular order is different.
 */
const DEFAULT_INTERNAL_NOTE =
  'Had discussion with multiple vendors for the same materials listed in this PO. '
  + 'But there is no mutual agreement on the terms & conditions between both parties. '
  + 'Hence going ahead with this trusted vendor for this PO who aligned with our T&Cs'

const newLine = (): PoLineDraft => ({
  key: crypto.randomUUID(), pr_line_id: null, material_id: null,
  quantity_stock: '', quantity_purchase: '', unit_rate: '', gst_rate_percent: '', specifications: '',
  rate_hint: null,
})

export function PoBuilder() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const { id: editId } = useParams()
  const editing = !!editId
  const [loadingDoc, setLoadingDoc] = useState(editing)
  // A form that failed to load must not be offered for editing.
  const [editBlocked, setEditBlocked] = useState(false)

  const role = profile!.role
  const { data: sites } = useSites()
  const { data: vendors } = useVendors()
  const { data: materials } = useMaterials()
  const { data: poTerms } = useTerms('po_terms')
  const { data: warrantyTerms } = useTerms('warranty_terms')
  const { data: paymentTerms } = useTerms('payment_terms')
  const { data: deliveryTerms } = useTerms('delivery_terms')

  const [openPrs, setOpenPrs] = useState<Array<{ pr_id: string; site_id: string; site_name: string; open_line_count: number; estimated_value: number }>>([])
  const [prId, setPrId] = useState(params.get('pr') ?? '')
  const [prLines, setPrLines] = useState<OpenPrLine[]>([])
  const [loadingPr, setLoadingPr] = useState(false)

  const [vendorId, setVendorId] = useState('')
  const [shipToSite, setShipToSite] = useState('')
  const [billToSite, setBillToSite] = useState('HO')
  const [docDate, setDocDate] = useState(todayIso)
  const [deliveryDate, setDeliveryDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 7); return iso(d) })
  const [quotationRef, setQuotationRef] = useState('Telephone/Verbal')
  const [quotationDate, setQuotationDate] = useState(iso(new Date()))
  // JV-financed sites only. The JV partner raises its own indent outside this
  // application; the Procurement Manager gets the number by phone, email or
  // WhatsApp and types it here. Empty and hidden for every ordinary site.
  const [indentNo, setIndentNo] = useState('')
  const [indentDate, setIndentDate] = useState('')
  const [paymentTerm, setPaymentTerm] = useState('45 Days from Invoice')
  // Some payment terms are templates — "X% Advance + Balance Before Dispatch".
  // The X is agreed per order, so it is captured here and substituted into the
  // text that gets stored and printed. The vendor must never see a literal X.
  const [placeholderValue, setPlaceholderValue] = useState('')
  const [deliveryTerm, setDeliveryTerm] = useState('FOR Destination')
  // Starts empty on purpose. It used to be pre-filled with "Handling, Loading,
  // Unloading charges are included in the price", which was the wrong place for
  // it twice over: a buyer who typed his own instruction lost the clause, and
  // one who did not was using a free-text box to carry a standing term.
  //
  // From 05-09-2026 that clause is STANDARD TERM 9 and prints on EVERY purchase
  // order, whatever anybody types here — which protects the company better than
  // a default that could be overwritten. This box now carries only what the
  // buyer means to say about this particular order.
  const [specialInstructions, setSpecialInstructions] = useState('')
  const [warranty, setWarranty] = useState('')
  // How long the order stays open, printed on the PDF. Seven days unless the
  // buyer says otherwise. Held as text so the box can be cleared and retyped
  // without React fighting the cursor; validated on save.
  const [validityDays, setValidityDays] = useState('7')
  // Why this vendor. Internal record only — it is deliberately absent from the
  // PDF's input type, so the renderer has no field to print it from, and
  // generate-po-pdf refuses to build a PDF if the text ever reaches its payload.
  const [internalNote, setInternalNote] = useState(DEFAULT_INTERNAL_NOTE)
  const [termIds, setTermIds] = useState<string[]>([])
  const [lines, setLines] = useState<PoLineDraft[]>([newLine()])
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)

  const materialById = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])
  const vendor = vendors.find((v) => v.id === vendorId) ?? null
  const billSite = sites.find((s) => s.id === billToSite) ?? null

  /**
   * The SHIP-TO site decides whether this order needs the JV partner's indent.
   *
   * Not the billing site. OVAL bills centrally from the head office on every
   * order, so "Bill to" reads OVAL Head Office even for a JV project — reading
   * that instead would switch the rule off for the one site it exists for.
   *
   * The same rule is enforced again in the database, on the way out of DRAFT.
   * This half is only here to be helpful: to show the boxes and to say so
   * before the order is sent, rather than after.
   */
  const shipSite = sites.find((s) => s.id === shipToSite) ?? null
  const needsIndent = !!shipSite?.requires_original_indent

  /** The master row behind the chosen payment term, if it has a placeholder. */
  const chosenPaymentTerm = useMemo(
    () => paymentTerms.find((t: any) => t.term_text === paymentTerm),
    [paymentTerms, paymentTerm])

  /**
   * The text that actually gets stored and printed, with the agreed figure
   * substituted for the X. A purchase order reaching a vendor still reading
   * "X% Advance" would be embarrassing at best.
   */
  const resolvedPaymentTerm = useMemo(() => {
    if (!chosenPaymentTerm?.placeholder_label || !placeholderValue.trim()) return paymentTerm
    return paymentTerm.replace(/\bX\b/, placeholderValue.trim())
  }, [paymentTerm, chosenPaymentTerm, placeholderValue])

  /**
   * The ticked clauses, in the sequence the T&C master defines.
   *
   * The printed order must read in a fixed logical sequence whatever order the
   * boxes were ticked in. Storing the tick order meant unticking and
   * re-ticking a clause moved it to the bottom of the vendor's copy.
   */
  const orderedTermIds = () =>
    poTerms.filter((t: any) => termIds.includes(t.id)).map((t: any) => t.id as string)

  // Every standard term is ticked by default — this matches the approved template.
  useEffect(() => { if (poTerms.length && termIds.length === 0) setTermIds(poTerms.map((t: any) => t.id)) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [poTerms])

  // 0212: a NEW order starts with the validity set on System Settings (7 until
  // changed). Once only, only while the box still holds the starting 7, and
  // never when correcting a saved order - that keeps its own.
  const { ready: settingsReady, poValidityDefaultDays } = useCompanySettings()
  const validityPrefilled = useRef(false)
  useEffect(() => {
    if (editing || validityPrefilled.current || !settingsReady) return
    validityPrefilled.current = true
    setValidityDays((v) => (v === '7' ? String(poValidityDefaultDays) : v))
  }, [editing, settingsReady, poValidityDefaultDays])

  // Correcting a purchase order the Head of Stores sent back at review.
  useEffect(() => {
    if (!editId) return
    void (async () => {
      setLoadingDoc(true)
      try {
        const { error: guard } = await supabase.rpc('rpc_assert_editable', {
          p_doc_type: 'PO', p_doc_id: editId,
        })
        if (guard) throw new Error(guard.message)

        const { data: h } = await supabase.from('purchase_orders').select('*').eq('id', editId).single()
        if (!h) throw new Error(`Purchase order ${editId} was not found.`)
        const { data: rows } = await supabase.from('po_line_items')
          .select('*').eq('po_id', editId).order('sl_no')
        const { data: chosen } = await supabase.from('po_selected_terms')
          .select('term_id').eq('po_id', editId)

        setPrId(h.pr_id ?? '')
        setVendorId(h.vendor_id)
        setShipToSite(h.site_id)
        setBillToSite(h.billing_site_id ?? 'HO')
        // Keep the order's own date on screen when correcting it, rather than
        // silently showing today for a document raised weeks ago.
        setDocDate(h.date_created ?? todayIso())
        setDeliveryDate(h.delivery_date ?? '')
        setQuotationRef(h.vendor_quotation_ref ?? '')
        setQuotationDate(h.vendor_quotation_date ?? '')
        setIndentNo(h.original_indent_no ?? '')
        setIndentDate(h.original_indent_date ?? '')
        // Prefer the template, so a resolved "30% Advance…" still lands on the
        // "X% Advance…" option with 30 in the box beside it.
        const tmpl = paymentTerms.find((t: any) => t.id === h.payment_term_id)
        setPaymentTerm(tmpl?.term_text ?? h.payment_terms ?? '')
        setPlaceholderValue(h.payment_term_x ?? '')
        setDeliveryTerm(h.delivery_terms ?? '')
        setSpecialInstructions(h.special_instructions ?? '')
        setWarranty(h.warranty ?? '')
        // Orders raised before 05-09-2026 carry no validity. Correcting one
        // should not silently give it a seven-day limit it was never issued
        // with, so the box starts empty and the buyer decides.
        setValidityDays(h.po_validity_days == null ? '' : String(h.po_validity_days))
        // An order raised before 05-09-2026 has no note. Show what the buyer
        // actually wrote — or nothing — rather than the standing text, which
        // would put words into his mouth on a document already in the chain.
        setInternalNote(h.internal_vendor_note ?? '')
        if (chosen?.length) setTermIds(chosen.map((t: any) => t.term_id))

        setLines((rows ?? []).map((r: any) => ({
          key: crypto.randomUUID(),
          pr_line_id: r.pr_line_id,
          material_id: r.material_id,
          quantity_stock: String(r.quantity_ordered ?? ''),
          quantity_purchase: String(r.quantity_in_purchase_uom ?? ''),
          unit_rate: String(r.unit_rate ?? ''),
          gst_rate_percent: String(r.gst_rate_percent ?? ''),
          specifications: r.item_specifications ?? '',
        })))
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not open this purchase order for correction')
        setEditBlocked(true)
      } finally { setLoadingDoc(false) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  // Only APPROVED / part-fulfilled PRs with open quantity may be picked.
  useEffect(() => {
    if (editing) return
    void (async () => {
      const { data } = await supabase.from('v_open_prs_for_po').select('*').order('date_created', { ascending: false })
      setOpenPrs((data ?? []) as any)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // In edit mode the lines belong to the order, not to the requisition.
    if (editing) return
    if (!prId) { setPrLines([]); return }
    setLoadingPr(true)
    void (async () => {
      const { data } = await supabase.from('v_open_pr_lines').select('*').eq('pr_id', prId).order('sl_no')
      const rows = (data ?? []) as OpenPrLine[]
      setPrLines(rows)
      if (rows[0]) setShipToSite(rows[0].site_id)
      setLines([newLine()])
      setLoadingPr(false)
    })()
  }, [prId])

  const gstTreatment = gstTreatmentFor(vendor, billSite)

  const computed = useMemo(() => lines.map((l) => {
    const m = l.material_id ? materialById[l.material_id] : null
    const qStock = Number(l.quantity_stock) || 0
    const qPurch = Number(l.quantity_purchase) || 0
    const rate = Number(l.unit_rate) || 0
    const gstRate = gstTreatment === 'NONE' ? 0 : (Number(l.gst_rate_percent) || 0)
    const lineAmount = round2(qPurch * rate)
    return { ...l, material: m, qStock, qPurch, rate, gstRate, line_amount: lineAmount,
             gst_amount: round2(lineAmount * gstRate / 100) }
  }), [lines, materialById, gstTreatment])

  const totals = splitGst(
    computed.map((c) => ({ line_amount: c.line_amount, gst_rate_percent: c.gstRate })),
    gstTreatment,
  )

  if (!can(role, 'createPo')) {
    return <NotPermitted what="create a purchase order" who="Procurement Executive" />
  }

  function update(key: string, patch: Partial<PoLineDraft>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  /** Keep the stock and purchase quantities in lock-step, in both directions. */
  function setStockQty(key: string, value: string) {
    const l = lines.find((x) => x.key === key)
    const m = l?.material_id ? materialById[l.material_id] : null
    const factor = m?.conversion_factor ?? 1
    update(key, {
      quantity_stock: value,
      quantity_purchase: value === '' ? '' : String(stockToPurchase(Number(value) || 0, factor)),
    })
  }

  function setPurchaseQty(key: string, value: string) {
    const l = lines.find((x) => x.key === key)
    const m = l?.material_id ? materialById[l.material_id] : null
    const factor = m?.conversion_factor ?? 1
    update(key, {
      quantity_purchase: value,
      quantity_stock: value === '' ? '' : String(purchaseToStock(Number(value) || 0, factor)),
    })
  }

  function pickMaterial(key: string, m: Material | null, prLine?: OpenPrLine) {
    if (!m) { update(key, { material_id: null, rate_hint: null }); return }
    const qStock = prLine ? prLine.quantity_open : 0
    update(key, {
      material_id: m.id,
      pr_line_id: prLine?.pr_line_id ?? null,
      quantity_stock: qStock ? String(qStock) : '',
      quantity_purchase: qStock ? String(stockToPurchase(qStock, m.conversion_factor)) : '',
      gst_rate_percent: String(m.gst_rate ?? 0),
      // The PR's estimate is per stock unit; this box is per purchase unit.
      unit_rate: prLine?.estimated_rate ? String(stockRateToPurchaseRate(prLine.estimated_rate, m.conversion_factor)) : '',
      specifications: prLine?.specifications && prLine.specifications !== 'NA' ? prLine.specifications : '',
      rate_hint: null,
    })
    void suggestRate(key, m.id)
  }

  /**
   * Offer what we last agreed to pay.
   *
   * The buyer can always overwrite it — a suggestion, not an instruction — but
   * nobody should have to go hunting through old orders to find out what this
   * vendor charged last time.
   */
  async function suggestRate(key: string, materialId: string) {
    const { data, error } = await supabase.rpc('rpc_suggest_po_rate', {
      p_material_id: materialId,
      p_site_id: shipToSite || null,
      p_vendor_id: vendorId || null,
    })
    if (error) return
    const row = Array.isArray(data) ? data[0] : data
    if (!row || row.rate == null) {
      update(key, { rate_hint: 'No previous purchase and no base rate — enter the quoted rate.' })
      return
    }
    setLines((ls) => ls.map((l) => l.key === key
      // Never overwrite a rate the buyer has already typed.
      ? { ...l, unit_rate: l.unit_rate ? l.unit_rate : String(row.rate), rate_hint: row.detail }
      : l))
  }

  /** Re-price every line when the vendor changes — a different vendor, a different rate. */
  useEffect(() => {
    if (editing || !vendorId) return
    for (const l of lines) {
      if (l.material_id && !l.unit_rate) void suggestRate(l.key, l.material_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, shipToSite])

  /** Pull one open PR line onto the order. Already-picked lines disappear. */
  function addFromPr(pl: OpenPrLine) {
    const m = materialById[pl.material_id]
    const key = crypto.randomUUID()
    setLines((ls) => {
      const empty = ls.find((l) => !l.material_id)
      const draft: PoLineDraft = {
        key: empty?.key ?? key,
        pr_line_id: pl.pr_line_id,
        material_id: pl.material_id,
        quantity_stock: String(pl.quantity_open),
        quantity_purchase: String(stockToPurchase(pl.quantity_open, m?.conversion_factor ?? pl.conversion_factor ?? 1)),
        // The PR's estimate is per stock unit; the PO rate is per purchase unit.
        unit_rate: pl.estimated_rate
          ? String(stockRateToPurchaseRate(pl.estimated_rate, m?.conversion_factor ?? pl.conversion_factor ?? 1))
          : '',
        gst_rate_percent: String(pl.gst_rate ?? m?.gst_rate ?? 0),
        specifications: pl.specifications && pl.specifications !== 'NA' ? pl.specifications : '',
      }
      return empty ? ls.map((l) => (l.key === empty.key ? draft : l)) : [...ls, draft]
    })
  }

  const usedPrLines = new Set(lines.map((l) => l.pr_line_id).filter(Boolean) as string[])

  /**
   * `submit` is passed in because ONE rule differs between saving and sending.
   *
   * A JV site's indent number is obtained offline, by telephone or WhatsApp.
   * Demanding it before the order can even be SAVED would mean re-keying the
   * whole order every time the JV partner does not pick up. So the draft may
   * be saved without it and cannot be submitted without it — which is also
   * exactly where the database trigger draws the line.
   */
  // Blank stays blank rather than becoming 0, so an empty box reads as "not
  // stated" and not as "expires the day it is issued".
  const validityNum = validityDays.trim() === '' ? NaN : Number(validityDays)

  function validate(submit: boolean): string | null {
    if (chosenPaymentTerm?.placeholder_label && !placeholderValue.trim()) {
      return `The payment term needs a figure — enter the ${chosenPaymentTerm.placeholder_label.toLowerCase()} agreed for this order.`
    }
    if (!vendorId) return 'Select the L1 vendor you settled on.'
    if (!shipToSite) return 'Select the site the material must be delivered to.'
    // The vendor is told on the order how long it stands. Only enforced when
    // the order is actually submitted, so a half-finished draft can still be
    // saved with the box cleared.
    if (submit) {
      if (!validityDays.trim()) return 'Enter how many days this purchase order stays valid. The usual figure is ' + poValidityDefaultDays + '.'
      if (!Number.isInteger(validityNum) || validityNum < 1 || validityNum > 365) {
        return 'The PO validity must be a whole number of days between 1 and 365.'
      }
    }
    if (submit && needsIndent) {
      const partner = shipSite?.jv_partner_name ?? 'the JV partner'
      if (!indentNo.trim()) {
        return `${shipSite?.site_name} is financed by a joint venture. Enter ${partner}'s Original Indent No before submitting this order.`
      }
      if (!indentDate) {
        return `Enter the Original Indent Date for indent ${indentNo.trim()}.`
      }
      if (indentDate > todayIso()) {
        return 'The original indent date cannot be in the future.'
      }
    }
    const filled = computed.filter((c) => c.material_id && c.qStock > 0)
    if (filled.length === 0) return 'Add at least one material with a quantity.'
    const noRate = filled.find((c) => c.rate <= 0)
    if (noRate) return `Enter the agreed rate for ${noRate.material?.item_name ?? 'every line'}.`
    const ids = filled.map((c) => c.material_id)
    if (new Set(ids).size !== ids.length) return 'The same material appears twice. Combine those lines.'
    return null
  }

  async function save(submit: boolean) {
    const problem = validate(submit)
    if (problem) {
      setError(problem)
      // Take him to it. The Submit button sits at the foot of a form that can
      // run to several screens, so an error drawn at the top is, from where he
      // is standing, nothing happening at all. Reported on the goods receipt
      // screen 09-09-2026; the same shape of form, the same trap.
      requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
      return
    }
    setError(null)
    setBusy(true)
    try {
      const filled = computed.filter((c) => c.material_id && c.qStock > 0)
      const payload = {
        header: {
          pr_id: prId || null,
          vendor_id: vendorId,
          site_id: shipToSite,
          billing_site_id: billToSite,
          status: submit ? 'SUBMITTED' : 'DRAFT',
          date_created: docDate,
          delivery_date: deliveryDate,
          prepared_by: profile!.id,
          creator_email: profile!.email,
          gst_treatment: gstTreatment,
          subtotal: totals.subtotal,
          cgst_amount: totals.cgst,
          sgst_amount: totals.sgst,
          igst_amount: totals.igst,
          gst_amount: totals.gst,
          total_amount: totals.total,
          total_in_words: amountInWords(totals.total),
          payment_terms: resolvedPaymentTerm || null,
          payment_term_id: chosenPaymentTerm?.id ?? null,
          payment_term_x: chosenPaymentTerm?.placeholder_label ? (placeholderValue.trim() || null) : null,
          delivery_terms: deliveryTerm || null,
          special_instructions: specialInstructions || null,
          po_validity_days: Number.isInteger(validityNum) ? validityNum : null,
          internal_vendor_note: internalNote.trim() || null,
          warranty: warranty || null,
          vendor_quotation_ref: quotationRef || null,
          vendor_quotation_date: quotationDate || null,
          // Gated on needsIndent, not on whether the boxes happen to hold
          // something. If a returned order is corrected by re-pointing it at
          // an ordinary site, the JV indent it used to carry must go with it —
          // otherwise the PDF would print a reference that no longer applies.
          original_indent_no: needsIndent ? (indentNo.trim() || null) : null,
          original_indent_date: needsIndent ? (indentDate || null) : null,
          was_submitted: submit,
        },
        lines: filled.map((c, i) => ({
          sl_no: i + 1,
          material_id: c.material_id,
          pr_line_id: c.pr_line_id,
          stock_uom: c.material!.unit_of_measure,
          purchase_uom: c.material!.purchase_uom,
          conversion_factor: c.material!.conversion_factor,
          quantity_ordered: round3(c.qStock),
          quantity_in_purchase_uom: round3(c.qPurch),
          unit_rate: c.rate,
          gst_rate_percent: c.gstRate,
          line_amount: c.line_amount,
          gst_amount: c.gst_amount,
          quantity_remaining: round3(c.qStock),
          item_specifications: c.specifications || null,
        })),
        term_ids: orderedTermIds(),
      }

      // ---- correcting a returned purchase order ----------------------------
      if (editing) {
        const { status: _s, prepared_by: _p, creator_email: _e, date_created: _d, ...editableHeader } = payload.header
        await reviseDocument({
          docType: 'PO',
          docId: editId!,
          header: editableHeader,
          lines: payload.lines,
          submit,
          comment: 'Corrected and resubmitted after being returned',
        })
        if (attachments.length) await queueAttachments('PO', editId!, attachments)
        // Terms are a separate child table, replaced the same way.
        await supabase.from('po_selected_terms').delete().eq('po_id', editId!)
        if (termIds.length) {
          // Order by the master list, NOT by the order the boxes were ticked.
          // Using the tick order meant unticking and re-ticking a clause moved
          // it to the bottom of the printed purchase order.
          await supabase.from('po_selected_terms').insert(
            orderedTermIds().map((t, i) => ({ po_id: editId!, term_id: t, sort_order: i + 1 })),
          )
        }
        toast.success(submit
          ? 'Corrected purchase order sent back for review.'
          : 'Correction saved. It stays with you until you submit it.')
        navigate(`/pos/${editId}`)
        return
      }

      const localId = `PO-LOCAL-${Date.now()}`
      await enqueue({
        kind: 'po.create', docType: 'PO',
        localDocId: localId,
        label: `${submit ? 'Submit' : 'Save'} purchase order to ${vendor?.company_name}`,
        payload,
      })
      if (attachments.length) await queueAttachments('PO', localId, attachments)

      toast.success(submit
        ? 'Purchase order submitted to the Head of Stores for the four-eyes review.'
        : 'Purchase order saved as a draft.')
      navigate('/pos')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the purchase order')
    } finally {
      setBusy(false)
    }
  }

  if (loadingDoc) return <Loading label="Opening the purchase order…" />

  if (editBlocked) {
    return (
      <div className="space-y-4">
        <Alert tone="error" title="This purchase order cannot be corrected">{error}</Alert>
        <button className="btn-secondary" onClick={() => navigate(`/pos/${editId}`)}>
          Back to the purchase order
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {editing && (
        <Alert tone="warning" title={`Correcting purchase order ${editId}`}>
          Make the changes that were asked for, then resubmit. The order keeps its number, so the
          vendor sees one document, not two.
        </Alert>
      )}
      {/* ---------------- source requisition ---------------- */}
      <Card
        title="Source requisition"
        subtitle={editing
          ? 'The requisition this order was raised against. It cannot be changed on a correction.'
          : 'Only requisitions the Head of Stores has approved, and that still carry open quantity, appear here'}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Against purchase requisition"
                 hint="Leave blank for an urgent, unplanned purchase with no requisition behind it.">
            <Select
              value={prId} onChange={(e) => setPrId(e.target.value)} disabled={editing}
              placeholder="— No requisition (urgent / unplanned purchase) —"
              options={editing && prId
                ? [{ value: prId, label: prId }]
                : openPrs.map((p) => ({
                    value: p.pr_id,
                    label: `${p.pr_id} · ${p.site_name} · ${p.open_line_count} open line(s)`,
                  }))}
            />
          </Field>
          {prId && (
            <div className="rounded-lg bg-oval-50 px-3 py-2 text-sm text-oval-900">
              <p className="font-semibold">{prLines.length} line(s) still open on {prId}</p>
              <p className="text-xs">
                Lines you have already put on this order are hidden. Anything left open stays available for
                a further purchase order against the same requisition.
              </p>
            </div>
          )}
        </div>

        {loadingPr && <Loading label="Loading requisition lines…" />}
        {prId && prLines.length > 0 && (
          <div className="mt-4 table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th><th>Material</th><th className="num">Open quantity</th>
                  <th className="num">Est. rate</th><th>Specification</th><th />
                </tr>
              </thead>
              <tbody>
                {prLines.map((pl) => {
                  const used = usedPrLines.has(pl.pr_line_id)
                  const m = materialById[pl.material_id]
                  return (
                    <tr key={pl.pr_line_id} className={used ? 'opacity-40' : ''}>
                      <td className="text-xs text-slate-400">{pl.sl_no}</td>
                      <td>
                        <span className="block text-sm font-medium">{pl.item_name}</span>
                        <span className="block font-mono text-xs text-slate-500">{pl.item_code}</span>
                      </td>
                      <td className="num">
                        {qty(pl.quantity_open)} {pl.stock_uom}
                        {needsConversion(m) && (
                          <span className="block text-xs text-oval-700">
                            = {qty(stockToPurchase(pl.quantity_open, m.conversion_factor))} {m.purchase_uom}
                          </span>
                        )}
                      </td>
                      <td className="num text-xs">
                        {pl.estimated_rate ? <>{rupees(pl.estimated_rate)} / {pl.stock_uom}</> : '—'}
                        {pl.estimated_rate > 0 && needsConversion(m) && (
                          <span className="block text-oval-700">
                            = {rupees(stockRateToPurchaseRate(pl.estimated_rate, m.conversion_factor))} / {m.purchase_uom}
                          </span>
                        )}
                      </td>
                      <td className="max-w-[220px] truncate text-xs text-slate-500">{pl.specifications ?? '—'}</td>
                      <td className="text-right">
                        <button className="btn-secondary btn-sm" disabled={used} onClick={() => addFromPr(pl)}>
                          {used ? 'Added' : 'Add to PO'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div ref={errorRef}>{error && <Alert tone="error">{error}</Alert>}</div>

      {/* ---------------- vendor & delivery ---------------- */}
      <Card title="Vendor and delivery" subtitle="The L1 vendor you selected after comparing quotations offline">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="L1 vendor" required className="lg:col-span-2">
            <Select
              value={vendorId} onChange={(e) => setVendorId(e.target.value)}
              placeholder="Select the vendor…"
              options={vendors.map((v) => ({
                value: v.id,
                label: `${v.company_name}${v.is_gst_registered ? '' : ' (unregistered)'} — ${v.state_name ?? ''}`,
              }))}
            />
            {vendor && (
              <p className="hint">
                {vendor.is_gst_registered
                  ? <>GSTIN {vendor.gstin} · {vendor.state_name}</>
                  : <>Not GST registered — <strong>no GST will be charged</strong>, exactly as on the approved PO format.</>}
              </p>
            )}
          </Field>

          <Field label="GST treatment" hint="Derived from the vendor's state against the billing state">
            <div className={`field font-semibold ${gstTreatment === 'NONE' ? 'text-slate-500' : 'text-oval-800'}`}>
              {gstTreatment === 'CGST_SGST' ? 'CGST + SGST (intra-state)'
                : gstTreatment === 'IGST' ? 'IGST (inter-state)'
                : 'Not applicable'}
            </div>
          </Field>

          <Field label="Ship to (site)" required>
            <Select
              value={shipToSite} onChange={(e) => setShipToSite(e.target.value)}
              placeholder="Select the delivery site…"
              options={sites.map((s) => ({ value: s.id, label: `${s.site_name} (${s.site_code})` }))}
            />
          </Field>
          <Field label="Bill to" hint="OVAL bills centrally from the head office">
            <Select
              value={billToSite} onChange={(e) => setBillToSite(e.target.value)}
              options={sites.map((s) => ({ value: s.id, label: s.site_name }))}
            />
          </Field>
          <DocumentDate label="Purchase order date" value={docDate} onChange={setDocDate} />
          <Field label="Delivery required by" required>
            <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
          </Field>
          <Field label="Vendor quotation reference">
            <Input value={quotationRef} onChange={(e) => setQuotationRef(e.target.value)}
                   placeholder="e.g. 304/2026-27, Telephone/Verbal, LAST PRICE" />
          </Field>
          <Field label="Quotation date">
            <Input type="date" value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)} />
          </Field>

          {/* ---- JV-financed sites only -------------------------------------
              Drawn immediately after the quotation pair, which is exactly
              where they print on the PDF, so the screen and the paper read in
              the same order. Absent entirely for the other eighteen sites —
              a disabled box nobody may fill is just a question mark. */}
          {needsIndent && (
            <>
              <Field label="Original Indent No" required
                     hint="Given to you by the JV partner — by telephone, email or WhatsApp. Printed on the purchase order.">
                <Input value={indentNo} onChange={(e) => setIndentNo(e.target.value)}
                       placeholder="e.g. ORBC/2026-27/117" />
              </Field>
              <Field label="Original Indent Date" required
                     hint="The date the JV partner raised that indent.">
                <Input type="date" value={indentDate} max={todayIso()}
                       onChange={(e) => setIndentDate(e.target.value)} />
              </Field>
              <div className="sm:col-span-2 lg:col-span-3">
                <Alert tone="info">
                  <strong>{shipSite?.site_name}</strong> is financed by a joint venture
                  {shipSite?.jv_partner_name ? <> — {shipSite.jv_partner_name}</> : null}.
                  Both boxes above must be filled before this order can be submitted for review.
                  You can still <strong>save it as a draft</strong> while you are waiting for the number.
                  {/* Who the vendor will invoice is the one thing on the printed
                      order that the buyer cannot see here and cannot change, so
                      the form says it out loud either way. Board 01-09-2026 set
                      every JV site to bill OVAL for the interim, and a notice
                      that still promised the JV's GSTIN would be worse than no
                      notice at all. */}
                  {shipSite?.bill_to_jv && shipSite?.jv_gstin ? (
                    <p className="mt-1">
                      This order will be <strong>billed to {shipSite.jv_partner_name}</strong>,
                      GSTIN <strong>{shipSite.jv_gstin}</strong> — not to OVAL’s own
                      GSTIN. The vendor’s tax invoice must be raised on that number.
                    </p>
                  ) : (
                    <p className="mt-1">
                      Under the board’s interim arrangement this order is still
                      <strong> billed to OVAL</strong>, in OVAL’s own name and GSTIN,
                      exactly like any other site. Only the indent reference above
                      is the JV’s.
                    </p>
                  )}
                </Alert>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* ---------------- line items ---------------- */}
      <Card
        title="Materials"
        subtitle="Quantities convert automatically between the stock UOM the store counts in and the purchase UOM the vendor bills in"
        actions={<button className="btn-secondary btn-sm" onClick={() => /* prepend: the blank row appears at the top so nobody scrolls to the bottom of a long document to type the next line. Agreed 09-09-2026. */ setLines((l) => [newLine(), ...l])}>
          <Plus className="h-4 w-4" /> Add line
        </button>}
      >
        <div className="space-y-3">
          {computed.map((c, i) => {
            const m = c.material
            const convert = needsConversion(m)
            return (
              <div key={c.key} className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">
                    LINE {i + 1}{c.pr_line_id && <span className="ml-2 pill-blue">from requisition</span>}
                  </span>
                  {lines.length > 1 && (
                    <button
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== c.key))}
                      aria-label="Remove line"
                    ><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>

                <div className="grid gap-3 lg:grid-cols-12">
                  <Field label="Material" required className="lg:col-span-4">
                    <MaterialPicker
                      value={c.material_id}
                      onChange={(m) => pickMaterial(c.key, m)}
                      exclude={lines.filter((x) => x.key !== c.key).map((x) => x.material_id!).filter(Boolean)}
                      autoFocus={false}
                    />
                  </Field>

                  <Field
                    label={`Quantity (${m?.unit_of_measure ?? 'stock UOM'})`}
                    required className="lg:col-span-2"
                    hint={convert ? 'What the store-man will count' : undefined}
                  >
                    <Input
                      type="number" inputMode="decimal" min="0" step="any"
                      value={c.quantity_stock}
                      onChange={(e) => setStockQty(c.key, e.target.value)}
                    />
                  </Field>

                  <Field
                    label={`Quantity (${m?.purchase_uom ?? 'purchase UOM'})`}
                    required className="lg:col-span-2"
                    hint={convert ? 'Printed on the PO and the challan' : undefined}
                  >
                    <Input
                      type="number" inputMode="decimal" min="0" step="any"
                      value={c.quantity_purchase}
                      onChange={(e) => setPurchaseQty(c.key, e.target.value)}
                      className={convert ? 'bg-oval-50/60' : ''}
                    />
                  </Field>

                  <Field label={`Rate per ${m?.purchase_uom ?? 'unit'}`} required className="lg:col-span-2"
                         hint={c.rate_hint ?? undefined}>
                    <Input
                      type="number" inputMode="decimal" min="0" step="any"
                      value={c.unit_rate}
                      onChange={(e) => update(c.key, { unit_rate: e.target.value })}
                    />
                  </Field>

                  <Field label="GST %" className="lg:col-span-1">
                    <Input
                      type="number" inputMode="decimal" min="0" step="any"
                      value={gstTreatment === 'NONE' ? '' : c.gst_rate_percent}
                      disabled={gstTreatment === 'NONE'}
                      placeholder={gstTreatment === 'NONE' ? 'NA' : ''}
                      onChange={(e) => update(c.key, { gst_rate_percent: e.target.value })}
                    />
                  </Field>

                  <Field label="Amount" className="lg:col-span-1">
                    <div className="field bg-slate-50 text-right text-sm font-semibold tabular-nums">
                      {rupees(c.line_amount)}
                    </div>
                  </Field>

                  <Field label="Specification printed on the PO" className="lg:col-span-12">
                    <Input
                      value={c.specifications}
                      onChange={(e) => update(c.key, { specifications: e.target.value })}
                      placeholder="e.g. MAKE: SHYAM STEEL | Test certificate mandatory"
                    />
                    <ConversionNote material={m} stockQty={c.qStock || undefined} />
                  </Field>
                </div>
              </div>
            )
          })}
        </div>

        {/* ---- totals ---- */}
        <div className="mt-5 flex justify-end">
          <div className="w-full max-w-md space-y-1.5 rounded-lg border border-slate-200 p-4">
            <Row label="Subtotal" value={rupees(totals.subtotal)} />
            {gstTreatment === 'NONE' && <Row label="GST" value="Not applicable (unregistered vendor)" muted />}
            {gstTreatment === 'CGST_SGST' && (
              <>
                <Row label="CGST" value={rupees(totals.cgst)} />
                <Row label="SGST" value={rupees(totals.sgst)} />
              </>
            )}
            {gstTreatment === 'IGST' && <Row label="IGST" value={rupees(totals.igst)} />}
            <div className="border-t border-slate-200 pt-2">
              <Row label="TOTAL" value={rupees(totals.total)} strong />
            </div>
            <p className="pt-1 text-xs italic text-slate-600">{amountInWords(totals.total)}</p>
          </div>
        </div>
      </Card>

      {/* ---------------- terms ---------------- */}
      <Card title="Terms &amp; conditions" subtitle="Printed on the purchase order exactly as selected here">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Payment terms">
            <Select
              value={paymentTerm}
              onChange={(e) => { setPaymentTerm(e.target.value); setPlaceholderValue('') }}
              options={paymentTerms.map((t: any) => ({ value: t.term_text, label: t.term_text }))}
            />
          </Field>

          {/*
            Only appears for the template terms. The vendor must never receive a
            purchase order that literally says "X% Advance", so the number is
            asked for here and substituted before anything is saved or printed.
          */}
          {chosenPaymentTerm?.placeholder_label ? (
            <Field
              label={chosenPaymentTerm.placeholder_label}
              required
              hint={placeholderValue.trim()
                ? `Will print as: ${resolvedPaymentTerm}`
                : 'This term has an X in it — give the figure agreed for this order.'}
            >
              <Input
                type="number" inputMode="decimal" step="any" min="0"
                value={placeholderValue}
                onChange={(e) => setPlaceholderValue(e.target.value)}
                placeholder="e.g. 30"
              />
            </Field>
          ) : null}
          <Field label="Delivery terms">
            <Select
              value={deliveryTerm} onChange={(e) => setDeliveryTerm(e.target.value)}
              options={deliveryTerms.map((t: any) => ({ value: t.term_text, label: t.term_text }))}
            />
          </Field>
          <Field label="Warranty">
            <Select
              value={warranty} onChange={(e) => setWarranty(e.target.value)}
              placeholder="— None —"
              options={[
                ...warrantyTerms.map((t: any) => ({ value: t.warranty_text, label: t.warranty_text })),
                { value: 'AS PER OEM', label: 'AS PER OEM' },
              ]}
            />
          </Field>
          <Field label="Special instructions">
            <Textarea rows={2} value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} />
          </Field>
          <Field label="PO validity (days)" required
                 hint={validityNum
                   ? `Prints as: "This PO is valid for ${validityNum} ${validityNum === 1 ? 'day' : 'days'} from the date of issue."`
                   : 'Between 1 and 365 days.'}>
            <Input type="number" min="1" max="365" step="1" value={validityDays}
                   onChange={(e) => setValidityDays(e.target.value)} />
          </Field>
        </div>

        <div className="mt-4">
          <p className="label">Standard terms to print</p>
          <div className="space-y-1.5">
            {poTerms.map((t: any) => (
              <label key={t.id} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 hover:bg-slate-50">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-oval-600"
                  checked={termIds.includes(t.id)}
                  onChange={(e) => setTermIds((ids) =>
                    e.target.checked ? [...ids, t.id] : ids.filter((x) => x !== t.id))}
                />
                <span className="text-xs text-slate-700">{t.term_text}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Internal only. Deliberately set apart from everything above it —
            every other field in this card is printed on the vendor's copy, and
            this one must never be. The amber border and the notice are there so
            nobody types into it thinking the vendor will read it. */}
        <div className="mt-6 rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50/60 p-4">
          <div className="mb-1 flex items-center gap-2">
            <p className="label mb-0">Why this vendor — internal record</p>
            <span className="pill-amber">Not printed on the PO</span>
          </div>
          <p className="mb-2 text-xs text-amber-800">
            Kept for the internal file and for audit. It is <strong>never</strong> shown to the
            vendor and never appears on the purchase order PDF. Edit it whenever the story on
            this particular order is different.
          </p>
          <Textarea rows={3} value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)} />
        </div>
      </Card>

      <Card title="Supporting documents"
            subtitle="The vendor's quotation this order was priced from, and any written approval behind it">
        <AttachmentCollector docType="PO" value={attachments} onChange={setAttachments} />
        {editing && (
          <p className="mt-2 text-xs text-slate-500">
            Files already on this order stay where they are. Anything you add here is added to them.
          </p>
        )}
      </Card>

      <div className="sticky bottom-20 z-10 flex flex-wrap justify-end gap-2 lg:bottom-4">
        <button className="btn-secondary" onClick={() => navigate(editing ? `/pos/${editId}` : '/pos')} disabled={busy}>
          Cancel
        </button>
        <button className="btn-secondary" onClick={() => save(false)} disabled={busy}>
          <Save className="h-4 w-4" /> {editing ? 'Save without submitting' : 'Save draft'}
        </button>
        <button className="btn-primary" onClick={() => save(true)} disabled={busy}>
          <Send className="h-4 w-4" /> {editing ? 'Resubmit for review' : 'Submit for review'}
        </button>
      </div>
    </div>
  )
}

function Row({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={strong ? 'text-sm font-bold text-slate-800' : 'text-sm text-slate-600'}>{label}</span>
      <span className={
        strong ? 'text-base font-bold tabular-nums text-slate-900'
          : muted ? 'text-xs italic text-slate-500'
          : 'text-sm tabular-nums text-slate-800'
      }>{value}</span>
    </div>
  )
}
